import { createRef, useState } from "react";
import { Form, Formik, FormikContextType } from "formik";
import clsx from "clsx";

// Components từ hệ thống của bạn
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import Button from "@app/components/button";

import useObservable from "@core/hooks/use-observable.hook";

// Giả sử bạn có AuthService quản lý việc này, nếu chưa có bạn có thể map fetch vào đây
import AuthService from "@app/services/http/auth.service";
import { confirmAccountValidationSchema } from "@app/validations";
import { openPortalDialog } from "@app/services/modal.service";
import ConfirmModal from "@app/components/confirm-modal";

interface ConfirmAccountFormValues {
  username: string;
  email: string;
}

function ConfirmAccountPage() {
  const formRef = createRef<FormikContextType<ConfirmAccountFormValues>>();
  const { subscribeOnce } = useObservable();

  const [serverMessage, setServerMessage] = useState<string>("");
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const handleConfirmAccount = (values: ConfirmAccountFormValues) => {
    setServerMessage(""); // Reset message cũ

    // Nếu bạn dùng AuthService (RxJS Observable) chuẩn của hệ thống:
    subscribeOnce(
      AuthService.forgetPassword({
        username: values.username,
        email: values.email,
      }),
      (res: any) => {
        handleResponse(res);
      }
    );
  };

  const handleResponse = (data: any) => {
    openPortalDialog(ConfirmModal, {
      message: data.message,
    });

    setServerMessage(data.message);

    if (data.statusCode === 200 || data.success || data.ok) {
      setIsSuccess(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#f2f2f2] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 w-[400px] max-w-full p-6 text-center transition-all duration-300">
        {/* Logo / Brand */}
        <h1 className="text-3xl font-black text-[#007bff] tracking-tight mb-2">
          PC Render
        </h1>

        <h2 className="text-xl font-bold text-gray-800 mb-3">
          Yêu cầu thay đổi mật khẩu
        </h2>

        {/* Hộp lưu ý */}
        <div className="bg-red-50 border border-red-100 rounded-xl p-3.5 mb-5 text-left">
          <p className="text-red-500 font-medium text-xs leading-relaxed">
            <span className="font-bold">Chú ý:</span> Bạn sẽ nhận được thư xác
            minh cập nhật mật khẩu qua email, vui lòng kiểm tra email của bạn
            sau khi ấn Xác nhận!
          </p>
        </div>

        {/* Formik Form */}
        <Formik
          initialValues={{ username: "", email: "" }}
          innerRef={formRef}
          onSubmit={handleConfirmAccount}
          validationSchema={confirmAccountValidationSchema}
          validateOnChange
          validateOnBlur
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4 text-left">
              {/* Trường Username */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Username
                </label>
                <FormControl name="username">
                  <Input
                    className="!max-w-none text-sm !h-10 rounded-lg"
                    errorClassName="text-sm mt-1 text-red-500"
                    placeholder="Nhập username"
                    disabled={isSuccess || isSubmitting}
                  />
                </FormControl>
              </div>

              {/* Trường Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Email
                </label>
                <FormControl name="email">
                  <Input
                    type="text"
                    className="!max-w-none text-sm !h-10 rounded-lg"
                    errorClassName="text-sm mt-1 text-red-500"
                    placeholder="Nhập email"
                    disabled={isSuccess || isSubmitting}
                  />
                </FormControl>
              </div>

              {/* Hiển thị lỗi từ server hoặc đếm ngược redirect */}
              {serverMessage && (
                <div
                  className={clsx(
                    "text-sm font-bold text-center mt-3 p-2 rounded-lg",
                    isSuccess
                      ? "text-green-600 bg-green-50"
                      : "text-red-500 bg-red-50"
                  )}
                >
                  {serverMessage}
                </div>
              )}

              {/* Nút Submit */}
              <div className="pt-2 flex justify-center">
                <Button
                  type="submit"
                  label={isSubmitting ? "Đang xử lý..." : "Xác nhận"}
                  disabled={isSubmitting || isSuccess}
                  containerClassName="w-full"
                  width="100%"
                  className={clsx(
                    "w-full !h-10 text-sm font-bold rounded-lg shadow-md transition-all duration-200",
                    isSubmitting || isSuccess
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#007bff] hover:bg-[#0056b3] text-white"
                  )}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default ConfirmAccountPage;
